import { invoke } from "@tauri-apps/api/core";
import { open } from "@tauri-apps/plugin-dialog";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useState } from "react";

import { Logger } from "@moment/logging";

import { $accessTokenFunc } from "../../internal/authenication";
import { $addDraftDocument } from "../../internal/document-list/document-list.atom";

interface ImportedDocumentResult {
	id: string;
	title: string;
	path: string;
	createdAt: string;
	updatedAt: string;
}

const logger = new Logger("useImportMarkdownRepo");

export const useImportMarkdownRepo = () => {
	const [isImportingMarkdownRepo, setIsImportingMarkdownRepo] = useState(false);
	const addDraftDocument = useSetAtom($addDraftDocument);
	const accessTokenFunc = useAtomValue($accessTokenFunc);

	const importMarkdownRepo = useCallback(async () => {
		try {
			setIsImportingMarkdownRepo(true);
			const title = "Select a folder to import";
			const selectedPath = await open({ title, directory: true, multiple: false });
			if (!selectedPath) {
				return;
			}

			// Used by the Rust side to probe the imported folder's "moment"
			// remote (if any). A missing token is fine — Rust treats that as
			// indeterminate and preserves the remote rather than destroying it.
			const token = await accessTokenFunc?.getAccessTokenSilently?.();

			const result = await invoke<ImportedDocumentResult>("import_markdown_repo", {
				path: selectedPath,
				token,
			});

			addDraftDocument({
				id: result.id,
				local: {
					[result.path]: {
						title: result.title,
						path: result.path,
						createdAt: new Date(result.createdAt),
						updatedAt: new Date(result.updatedAt),
					},
				},
				remote: {},
			});

			return result;
		} catch (error) {
			logger.error("Error importing document", { error });
			throw error;
		} finally {
			setIsImportingMarkdownRepo(false);
		}
	}, [addDraftDocument, accessTokenFunc]);

	return { importMarkdownRepo, isImportingMarkdownRepo };
};
